/**
 * The CLIENT half of the C15 Lexicon seam (Normal Core MASTER-PRD C15,
 * GROUNDING-PRD). `vocab()` itself is pure and server-resolvable (a server
 * component calls `resolveWorkspaceLexicon(workspaceId)` then `vocab(lexicon,
 * key)`). But client surfaces — the Play driver, the mode rail, any interactive
 * block — can't reach the DB. This provider lifts the workspace's resolved
 * `WorkspaceLexicon` (resolved ONCE in the root layout, a server component) into
 * the client tree so `useVocab()` returns a bound resolver with the SAME total
 * fallback to canonical defaults as the server path.
 *
 * The direction of translation runs toward the user: agents/slugs/tables stay
 * canonical; surfaces *speak* the Lexicon. A law firm's `noun:customer` renders
 * "Clients", `noun:commitment` renders "Matters" — and with no lexicon set,
 * every key falls back to the canonical label (the chain is total, never throws).
 */

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
// Narrowed from the `@excellent/core` barrel to the `vocab` leaf (RO-0-05). Same
// values, but the barrel re-exports `objectives` → `events/engine` → `db` →
// `security/{keystore,keychain}`, so importing it from a component pulls the
// SQLCipher key path and the OS keychain into any browser bundle that includes
// this provider. Under Next `server-only` was the wall; in the Vite SPA there is
// none (PRD §5 I4 / R1). The leaf is 3 modules with zero `node:*` imports.
import { vocab, type WorkspaceLexicon, type VocabOptions, type VocabKey } from "@excellent/core/vocab";

const VocabContext = createContext<WorkspaceLexicon | null>(null);

/**
 * Provide the workspace's resolved Lexicon to the client tree. `lexicon` is
 * resolved server-side in the root layout (`resolveWorkspaceLexicon`); pass
 * `null` and every term falls back to its canonical default.
 */
export function VocabProvider({
  lexicon,
  children,
}: {
  lexicon: WorkspaceLexicon | null;
  children: ReactNode;
}) {
  // Freeze the value identity by serialized content so a fresh-object lexicon
  // from the server render doesn't thrash consumers every navigation.
  const key = lexicon ? JSON.stringify(lexicon) : null;
  const value = useMemo(() => lexicon, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return <VocabContext.Provider value={value}>{children}</VocabContext.Provider>;
}

/**
 * The C15 client hook. Returns a bound resolver: `t("noun:customer", {count:1})`
 * → the workspace's grounded singular ("Client"), or the canonical default
 * ("Customer") when no lexicon is set. Pure + total — safe to call anywhere,
 * even outside a provider (falls back to canonical).
 */
export function useVocab(): (key: VocabKey, opts?: VocabOptions) => string {
  const lexicon = useContext(VocabContext);
  return useMemo(
    () => (key: VocabKey, opts?: VocabOptions) => vocab(lexicon, key, opts),
    [lexicon],
  );
}

/** Read the raw Lexicon from context (null when unset / outside a provider). */
export function useLexicon(): WorkspaceLexicon | null {
  return useContext(VocabContext);
}
